import React, { useMemo } from "react";
import { useLocation } from "react-router";
import { MoreOutlined } from "@ant-design/icons";
import { Tag } from "antd";
import { dateStringToStandardFormat } from "@/utils/dateFormatter";
import { ShipmentStatus } from "@/utils/enum";
import { formatPrice, formatDistance, getGuidFirst8 } from "@/utils/utils";
import useScreenDetector from "@/hooks/useScreenDetector";
import Badge from "@/components/Badge";
import SecurePassTag from "@/components/SecurePass/SecurePassTag/SecurePassTag";
import ShareMyRide from "@/components/ShareMyRide";
import ViewVehicles from "@/components/ViewVehicles";
import styles from "../style.less";

function columnShipmentId(isMobile, securePassEnabled) {
  return {
    title: "Shipment Id",
    dataIndex: "id",
    minWidth: isMobile ? 80 : 110,
    render: (text, record) => (
      <>
        <span className="span-click">
          {getGuidFirst8(text)}{" "}
          {record.isInternalShipment && (
            <Tag color="blue" style={{ display: "table" }}>
              Internal
            </Tag>
          )}
        </span>
        {securePassEnabled && (
          <>
            <br />
            <SecurePassTag tagStatus={record?.securePass} compact />
          </>
        )}
      </>
    ),
  };
}

const columnCreatedAt = {
  title: "Created At",
  dataIndex: "creationTime",
  minWidth: 140,
  render: (text, record) => (
    <span className="span-click">{dateStringToStandardFormat(text, { toTimeZone: record.label })}</span>
  ),
};

const columnPostedAt = {
  title: "Posted at",
  dataIndex: "postedAt",
  minWidth: 130,
  render: (text, record) => (
    <span className="span-click">{dateStringToStandardFormat(text, { toTimeZone: record.label })}</span>
  ),
};

function columnDealerName(isMobile) {
  return {
    title: "Dealer Name",
    dataIndex: "dealerName",
    minWidth: isMobile ? 80 : 160,
    render: (text, record) => <span className="span-click">{text || record?.entity?.name || ""}</span>,
  };
}

const columnPickupDropoffLocation = {
  title: "Pickup/Dropoff Location",
  dataIndex: "pickupDropoffLocation",
  minWidth: 180,
  render: (_, record) => (
    <div
      className="orig-to-dest"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <div className={styles.landingPageTableData}>{record.pickUpAddress}</div>
      <MoreOutlined
        style={{
          position: "relative",
          right: 4,
          paddingTop: "1%",
          paddingBottom: "1%",
        }}
      />
      <div className={styles.landingPageTableData}>{record.dropOffAddress}</div>
    </div>
  ),
};

const getTotalDealerPrice = (text, record) => {
  if (record.isInternalShipment) {
    return "$0";
  }
  return !text || text === "-" ? "-" : formatPrice(text);
};

const columnDealerPrice = {
  title: "Price",
  dataIndex: "totalDealerPrice",
  minWidth: 100,
  render: (text, record) => <span className="span-click">{getTotalDealerPrice(text, record)}</span>,
};

const columnDistance = {
  title: "Distance in Miles",
  dataIndex: "distance",
  minWidth: 100,
  render: text => <span className="span-click">{formatDistance(text)}</span>,
};

const columnPickupAt = {
  title: "Picked up at",
  dataIndex: "pickupAt",
  minWidth: 100,
  render: (_, record) => (
    <span className="span-click">
      {dateStringToStandardFormat(record.loadPickedUpAt, { toTimeZone: record.label })}
    </span>
  ),
};

const columnDropoffAt = {
  title: "Dropped off at",
  dataIndex: "dropoffAt",
  minWidth: 100,
  render: (_, record) => (
    <span className="span-click">{dateStringToStandardFormat(record.loadDropOffAt, { toTimeZone: record.label })}</span>
  ),
};

const columnPickupEta = {
  title: "Pick up ETA",
  dataIndex: "pickupEta",
  minWidth: 100,
  render: (_, record) => (
    <span className="span-click">
      {dateStringToStandardFormat(record.loadPickupETA, { toTimeZone: record.label }) || "Pending"}
    </span>
  ),
};

const columnDropoffEta = {
  title: "Drop off ETA",
  dataIndex: "dropoffEta",
  minWidth: 100,
  render: (_, record) => (
    <span className="span-click">
      {dateStringToStandardFormat(record.loadDropOffETA, { toTimeZone: record.label }) || "Pending"}
    </span>
  ),
};

const columnVehiclesCount = {
  title: "Vehicles",
  dataIndex: "vehiclesCount",
  minWidth: 170,
  render: (_, record) => <ViewVehicles vehicles={record.vehicles || []} />,
};

const columnStatus = {
  title: "Status",
  dataIndex: "status",
  minWidth: 146,
  render: (text, record) => {
    const exchangeId = record.childShipmentId || record.parentShipmentId;

    const statusMap = {
      [ShipmentStatus.Draft]: { status: "default", text: "Draft" },
      [ShipmentStatus.Pending]: {
        status: "processing",
        text: "Pending Quotes",
      },
      [ShipmentStatus.Completed]: {
        status: "processing",
        text: "Ready to Post",
      },
      [ShipmentStatus.Posted]: { status: "processing", text: "Posted" },
      [ShipmentStatus.Assigned]: {
        status: "processing",
        text: "Assign/In-Transit",
      },
      [ShipmentStatus.Delivered]: { status: "success", text: "Delivered" },
      [ShipmentStatus.Incomplete]: { status: "error", text: "Cancelled" },
    };

    return (
      <span className="span-click">
        <Badge
          status={statusMap[text].status}
          text={statusMap[text].text}
          style={{ marginTop: exchangeId ? "18px" : 0 }}
        />
      </span>
    );
  },
};

const columnDeliveredStatus = {
  title: "Status",
  dataIndex: "status",
  minWidth: 146,
  render: (_, record) => (
    <span className="span-click">
      <Badge status="success" text="Delivered" />
      {(record.status === ShipmentStatus.Completed || record.status === ShipmentStatus.Posted) && (
        <ShareMyRide shipmentId={record.shipmentId} />
      )}
    </span>
  ),
};

function columnDealerTrade(isMobile) {
  return {
    title: "",
    dataIndex: "ribbon",
    minWidth: 1,
    width: 1,
    responsive: 0,
    render: (_, record) => {
      const exchangeId = record.childShipmentId || record.parentShipmentId;
      return exchangeId ? (
        <div
          style={{
            position: "absolute",
            top: "-0.0625rem",
            right: "-0.0625rem",
            padding: `${isMobile ? "0.125rem" : "0.25rem"} 0.625rem`,
            backgroundColor: "#2d6ac4",
            color: "#fff",
            fontSize: isMobile ? "0.625rem" : "0.75rem",
            borderTopRightRadius: "0.625rem",
            borderBottomLeftRadius: "0.25rem",
            maxWidth: "200px",
            whiteSpace: "nowrap",
          }}
          onClick={e => {
            e.stopPropagation();
            window.location.href = `/shipments/dealer/all/view?shipmentId=${exchangeId}`;
          }}
        >
          {`Trade With: ${exchangeId.split("-")[0].toUpperCase()}`}
        </div>
      ) : null;
    },
  };
}

const columnShareMyRide = {
  title: "",
  dataIndex: "ribbon",
  minWidth: 1,
  width: 1,
  responsive: 0,
  render: (_, record) => {
    const shareable = [ShipmentStatus.Completed, ShipmentStatus.Posted, ShipmentStatus.Assigned];
    const canShare = shareable.includes(record.status);

    return canShare ? <ShareMyRide shipmentId={record.id} style={{ marginTop: 0 }} /> : null;
  },
};

const useShipmentsListTableColumns = ({ filterStatus, securePassEnabled }) => {
  const { pathname } = useLocation();
  const { isMobile } = useScreenDetector(480);

  return useMemo(() => {
    if (pathname === null) {
      return [];
    }

    const displayColumns = [];

    displayColumns.push(columnShipmentId(isMobile, securePassEnabled));

    if (filterStatus === null) {
      displayColumns.push(columnCreatedAt);
    }

    displayColumns.push(columnPostedAt);
    displayColumns.push(columnDealerName(isMobile));
    displayColumns.push(columnPickupDropoffLocation);
    displayColumns.push(columnDealerPrice);
    displayColumns.push(columnDistance);
    displayColumns.push(columnPickupAt);
    displayColumns.push(columnDropoffAt);
    displayColumns.push(columnPickupEta);
    displayColumns.push(columnDropoffEta);
    displayColumns.push(columnVehiclesCount);

    if (filterStatus === ShipmentStatus.Delivered) {
      displayColumns.push(columnDeliveredStatus);
    } else {
      displayColumns.push(columnStatus);
    }

    displayColumns.push(columnShareMyRide);

    displayColumns.push(columnDealerTrade(isMobile));

    return displayColumns;
  }, [filterStatus, pathname, isMobile, securePassEnabled]);
};

export default useShipmentsListTableColumns;
